import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Typography,
  Switch,
  Alert,
  Button,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
  InputLabel,
  Divider,
} from "@mui/material";
import { AlertTitle } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import Div from "@jumbo/shared/Div/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { Field, useFormikContext } from "formik";
import { Label } from "app/pages/presentation/module/form/types";
import {
  DEVICE_HEALTH_CHECK_OPTIONS,
  LANGUAGES,
} from "app/utils/constants/settings";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import ClearIcon from "@mui/icons-material/Clear";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder/JumboListNoDataPlaceholder";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import * as Yup from "yup";
import { format, parse } from "date-fns";
import CachedIcon from "@mui/icons-material/Cached";
import { triggerDSOKBuild } from "app/services/api/management";
import { useSnackbar } from "notistack";
import { Link } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const FIELD_NAME = "LOCATIONS_FIELD";

export const getLocationsFieldSchema = (locations) => {
  const schema = {};
  const locationKeys = Object.keys(locations || {});
  if (!locationKeys?.length) {
    return schema;
  }
  locationKeys.forEach((location) => {
    schema[location] = {
      code: "",
      languageCode: "sk",
      country_id: "",
      street: "",
      postcode: "",
      city: "",
      firstname: "",
      lastname: "",
      telephone: "",
    };
  });
  return schema;
};

export const getLocationsFieldValidationSchema = (t) => ({
  code: Yup.string().required(t("messages.checkRequiredFields")),
  languageCode: Yup.string().required(t("messages.checkRequiredFields")),
  country_id: Yup.string().required(t("messages.checkRequiredFields")),
  street: Yup.string().required(t("messages.checkRequiredFields")),
  postcode: Yup.string().required(t("messages.checkRequiredFields")),
  city: Yup.string().required(t("messages.checkRequiredFields")),
  firstname: Yup.string().required(t("messages.checkRequiredFields")),
  lastname: Yup.string().required(t("messages.checkRequiredFields")),
  telephone: Yup.string().required(t("messages.checkRequiredFields")),
});

const SmartkioskLocationsField = ({
  handleChange,
  expanded,
  isPartner,
  isAdmin,
  isPartnerAdmin,
  customer,
  hideSectionTitle = false,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const [selectedLocation, setSelectedLocation] = useState("1");
  const [updating, setUpdating] = useState();
  const [locationSectionTab, setLocationSectionTab] = useState("basic");
  const { showDialog, hideDialog } = useJumboDialog();
  const { enqueueSnackbar } = useSnackbar();
  const customerCcode = customer?.dsokSettings?.customer?.storeCode;
  let env = values?.customer?.beDomain?.includes("staging") ? "stage" : "prod";
  const orderStatusScreenUrl = `${window.location.origin}/order-status-screen/${values?.customer?.code}/${values?.customer?.locations[selectedLocation]?.code}/${env}/sk-SK/15000/restaurant`;

  const datavisionPosEnabled = values?.customer?.datavisionPosEnabled === true;
  const pickupDisplayEnabled = values?.customer?.pickupDisplayEnabled === true;
  const kitchenDisplayEnabled =
    values?.customer?.kitchenDisplayEnabled === true;
  const showPickupDisplayButton = datavisionPosEnabled && pickupDisplayEnabled;

  const locationKeys = Object.keys(values.customer.locations || {});

  const handleAddLocation = () => {
    setFieldValue("customer.locations", {
      ...values.customer.locations,
      [locationKeys.length + 1]: { languageCode: "sk" },
    });
  };

  const handleDeleteLocation = (location) => {
    const newLocations = {};
    let deleted = 0;
    locationKeys.forEach((key, index) => {
      if (key === location) {
        deleted++;
        return;
      }
      newLocations[index + 1 - deleted] = values.customer.locations[key];
    });
    setFieldValue("customer.locations", newLocations);
    setSelectedLocation(locationKeys[0]);
    hideDialog();
  };

  const handleBuildApp = async (url) => {
    setUpdating(true);
    try {
      await triggerDSOKBuild(url);
      const message = `${t("buttons.buildApp")} ${t(
        "notifications.wasSuccessful",
      )}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
    } catch (error) {
      const message = `${t("buttons.buildApp")} ${t(
        "notifications.wasFailed",
      )}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
    }
    setUpdating(false);
    hideDialog();
  };

  const content = (
    <Stack direction="row" alignItems="center" width="100%" spacing={1} px={2}>
      <TabContext value={selectedLocation}>
        <Stack width="100%">
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
            }}
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
          >
            <TabList onChange={(_, val) => setSelectedLocation(val)}>
              {locationKeys.map((item) => {
                return (
                  <Tab
                    key={item}
                    label={
                      <Stack direction="row" sx={{ alignItems: "center" }}>
                        {item}
                        {!isPartner && (
                          <IconButton
                            onClick={() =>
                              showDialog({
                                variant: "confirm",
                                title: t("widgets.confirmDialog.areYouSure"),
                                onYes: () => handleDeleteLocation(item),
                                onNo: () => hideDialog(),
                              })
                            }
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    }
                    value={item}
                  />
                );
              })}
            </TabList>
            {!isPartnerAdmin && (
              <IconButton onClick={handleAddLocation}>
                <AddCircleIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          {!locationKeys?.length ? (
            <JumboListNoDataPlaceholder />
          ) : (
            locationKeys.map((location) => {
              return (
                <TabPanel
                  key={`tab_${location}`}
                  value={location}
                  sx={{
                    pb: 1,
                    pr: 0,
                    pl: 0,
                  }}
                >
                  <TabContext value={locationSectionTab}>
                    <Stack
                      direction="row"
                      width="100%"
                      spacing={0}
                      sx={{ alignItems: "stretch" }}
                    >
                      <Box
                        sx={{
                          borderRight: 1,
                          borderColor: "divider",
                          minWidth: 200,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <TabList
                          orientation="vertical"
                          onChange={(_, val) => setLocationSectionTab(val)}
                          sx={{
                            flex: 1,
                            "& .MuiTabList-flexContainer": {
                              flexDirection: "column",
                              alignItems: "stretch",
                            },
                          }}
                        >
                          <Tab
                            label={t("pages.dsokDetails.locationsSectionBasic")}
                            value="basic"
                            sx={{
                              textTransform: "none",
                              alignItems: "flex-start",
                            }}
                          />
                          <Tab
                            label={t("pages.dsokDetails.locationsSectionOther")}
                            value="other"
                            sx={{
                              textTransform: "none",
                              alignItems: "flex-start",
                            }}
                          />
                          <Tab
                            label={t("pages.dsokDetails.locationsSectionNotifications")}
                            value="notifications"
                            sx={{
                              textTransform: "none",
                              alignItems: "flex-start",
                            }}
                          />
                        </TabList>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0, pl: 2 }}>
                        <TabPanel value="basic" sx={{ p: 0, height: "100%" }}>
                          <Grid container spacing={1}>
                            <Grid item xs={12} md={4} lg={4}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  fullWidth
                                  required
                                  name={`customer.locations.${location}.name`}
                                  label={t("pages.dsokDetails.locationName")}
                                  InputProps={{
                                    readOnly: isPartner,
                                  }}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={4} lg={4}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  fullWidth
                                  required
                                  name={`customer.locations.${location}.code`}
                                  label={t("pages.dsokDetails.code")}
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={2} lg={2}>
                              <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                                <JumboTextField
                                  required
                                  select
                                  InputLabelProps={{ required: false }}
                                  InputProps={{
                                    readOnly: isPartner,
                                  }}
                                  name={`customer.locations.${location}.languageCode`}
                                  label={
                                    <Label
                                      text={t("pages.dsokDetails.language")}
                                      required
                                    />
                                  }
                                >
                                  {LANGUAGES.map((language, index) => {
                                    return (
                                      <MenuItem
                                        key={index}
                                        value={language.code}
                                      >
                                        {language.name}
                                      </MenuItem>
                                    );
                                  })}
                                </JumboTextField>
                              </FormControl>
                            </Grid>
                            {!isPartner && (
                              <Grid item xs={12} md={2} lg={2}>
                                <Div sx={{ mt: 1, mb: 1 }}>
                                  <JumboTextField
                                    required
                                    fullWidth
                                    name={`customer.locations.${location}.country_id`}
                                    label={t("pages.dsokDetails.countryId")}
                                  />
                                </Div>
                              </Grid>
                            )}

                            <Grid item xs={12} md={12} lg={12}></Grid>
                            <Grid item xs={12} md={4} lg={4}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  fullWidth
                                  name={`customer.locations.${location}.ico`}
                                  InputProps={{
                                    readOnly: isPartner,
                                  }}
                                  label={t("pages.dsokDetails.ico")}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={4} lg={4}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  fullWidth
                                  name={`customer.locations.${location}.dic`}
                                  InputProps={{
                                    readOnly: isPartner,
                                  }}
                                  label={t("pages.dsokDetails.dic")}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={4} lg={4}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  fullWidth
                                  name={`customer.locations.${location}.icDph`}
                                  InputProps={{
                                    readOnly: isPartner,
                                  }}
                                  label={t("pages.dsokDetails.icDPH")}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}></Grid>
                            <Grid item xs={12} md={4} lg={4}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  required
                                  fullWidth
                                  name={`customer.locations.${location}.street`}
                                  InputProps={{
                                    readOnly: isPartner,
                                  }}
                                  label={t("pages.editDevice.street")}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={4} lg={4}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  required
                                  fullWidth
                                  InputProps={{
                                    readOnly: isPartner,
                                  }}
                                  name={`customer.locations.${location}.postcode`}
                                  label={t("pages.editDevice.zip")}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={4} lg={4}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  required
                                  fullWidth
                                  InputProps={{
                                    readOnly: isPartner,
                                  }}
                                  name={`customer.locations.${location}.city`}
                                  label={t("pages.editDevice.city")}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}></Grid>
                            {!isPartner && (
                              <>
                                <Grid item xs={12} md={4} lg={4}>
                                  <Div sx={{ mt: 1, mb: 1 }}>
                                    <JumboTextField
                                      required
                                      fullWidth
                                      name={`customer.locations.${location}.firstname`}
                                      label={t("pages.dsokDetails.firstname")}
                                    />
                                  </Div>
                                </Grid>
                                <Grid item xs={12} md={4} lg={4}>
                                  <Div sx={{ mt: 1, mb: 1 }}>
                                    <JumboTextField
                                      fullWidth
                                      required
                                      name={`customer.locations.${location}.lastname`}
                                      label={t("pages.dsokDetails.lastname")}
                                    />
                                  </Div>
                                </Grid>
                                <Grid item xs={12} md={4} lg={4}>
                                  <Div sx={{ mt: 1, mb: 1 }}>
                                    <JumboTextField
                                      required
                                      fullWidth
                                      name={`customer.locations.${location}.telephone`}
                                      label={t("pages.editDevice.phone")}
                                    />
                                  </Div>
                                </Grid>
                              </>
                            )}
                            <Grid item xs={12} md={12} lg={12}></Grid>
                            <Grid item xs={12} md={2} lg={2}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <LocalizationProvider
                                  dateAdapter={AdapterDateFns}
                                >
                                  <TimePicker
                                    required
                                    ampm={false}
                                    sx={{ width: "100%" }}
                                    name={`customer.locations.${location}.timeFrom`}
                                    label={
                                      <Label
                                        text={t("pages.dsokDetails.timeFrom")}
                                        helpText={t(
                                          "pages.dsokDetails.timeFromHelp",
                                        )}
                                        sx={{ color: "text.primary" }}
                                        required={false}
                                      />
                                    }
                                    slotProps={{
                                      textField: {
                                        InputLabelProps: { shrink: true },
                                      },
                                    }}
                                    value={parse(
                                      values.customer.locations[location]
                                        .timeFrom,
                                      "HH:mm:ss",
                                      new Date(),
                                    )}
                                    onChange={(newValue) =>
                                      setFieldValue(
                                        `customer.locations.${location}.timeFrom`,
                                        format(newValue, "HH:mm:ss"),
                                      )
                                    }
                                    readOnly={isPartnerAdmin}
                                  />
                                </LocalizationProvider>
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={2} lg={2}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <LocalizationProvider
                                  dateAdapter={AdapterDateFns}
                                >
                                  <TimePicker
                                    required
                                    ampm={false}
                                    sx={{ width: "100%" }}
                                    name={`customer.locations.${location}.timeTo`}
                                    label={t("pages.dsokDetails.timeTo")}
                                    slotProps={{
                                      textField: {
                                        InputLabelProps: { shrink: true },
                                      },
                                    }}
                                    value={parse(
                                      values.customer.locations[location]
                                        .timeTo,
                                      "HH:mm:ss",
                                      new Date(),
                                    )}
                                    onChange={(newValue) =>
                                      setFieldValue(
                                        `customer.locations.${location}.timeTo`,
                                        format(newValue, "HH:mm:ss"),
                                      )
                                    }
                                    readOnly={isPartnerAdmin}
                                  />
                                </LocalizationProvider>
                              </Div>
                            </Grid>
                            {/* Device Health Monitoring */}
                            <Grid item xs={12} md={4} lg={4}>
                              <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
                                <InputLabel>
                                  {t("pages.dsokDetails.deviceHealthCheck")}
                                </InputLabel>
                                <Select
                                  multiple
                                  value={
                                    values.customer.locations[location]
                                      .deviceHealthCheck || []
                                  }
                                  onChange={(e) =>
                                    setFieldValue(
                                      `customer.locations.${location}.deviceHealthCheck`,
                                      e.target.value,
                                    )
                                  }
                                  input={
                                    <OutlinedInput
                                      label={t(
                                        "pages.dsokDetails.deviceHealthCheck",
                                      )}
                                    />
                                  }
                                  renderValue={(selected) =>
                                    selected
                                      .map(
                                        (val) =>
                                          DEVICE_HEALTH_CHECK_OPTIONS.find(
                                            (opt) => opt.value === val,
                                          )?.label || val,
                                      )
                                      .join(", ")
                                  }
                                >
                                  {DEVICE_HEALTH_CHECK_OPTIONS.map((option) => (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      <Checkbox
                                        checked={
                                          (
                                            values.customer.locations[location]
                                              .deviceHealthCheck || []
                                          ).indexOf(option.value) > -1
                                        }
                                      />
                                      <ListItemText primary={option.label} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}></Grid>
                            <Grid item xs={12} md={6} lg={6}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  fullWidth
                                  name={`customer.locations.${location}.token`}
                                  label={t("pages.dsokDetails.token")}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={3} lg={3}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  fullWidth
                                  name={`customer.locations.${location}.username`}
                                  label={t("pages.dsokDetails.username")}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={3} lg={3}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  fullWidth
                                  name={`customer.locations.${location}.password`}
                                  label={t("pages.dsokDetails.password")}
                                />
                              </Div>
                            </Grid>
                          </Grid>
                        </TabPanel>
                        <TabPanel value="other" sx={{ p: 0, height: "100%" }}>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              {isAdmin && (
                                <>
                                  <Alert variant="outlined" severity="info" sx={{ mt: 1, mb: 1, "& .MuiAlert-message": { whiteSpace: "pre-line" } }}>
                                    {t("pages.dsokDetails.displayButtonsHelpText")}
                                  </Alert>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ mt: 1, mb: 1 }}
                                    flexWrap="wrap"
                                    useFlexGap
                                  >
                                    <Div>
                                      {showPickupDisplayButton ? (
                                        <Link
                                          to={orderStatusScreenUrl}
                                          style={{
                                            textDecoration: "none",
                                            alignSelf: "center",
                                          }}
                                          target="_blank"
                                        >
                                          <Button
                                            variant="contained"
                                            startIcon={<OpenInNewIcon />}
                                            sx={{
                                              boxShadow: "none",
                                              height: "38px",
                                              whiteSpace: "nowrap",
                                            }}
                                            size="small"
                                          >
                                            {t(
                                              "pages.dsokDetails.pickupDisplayButtonLabel",
                                            )}
                                          </Button>
                                        </Link>
                                      ) : (
                                        <Button
                                          variant="contained"
                                          startIcon={<OpenInNewIcon />}
                                          sx={{
                                            boxShadow: "none",
                                            height: "38px",
                                            whiteSpace: "nowrap",
                                          }}
                                          size="small"
                                          disabled
                                        >
                                          {t(
                                            "pages.dsokDetails.pickupDisplayButtonLabel",
                                          )}
                                        </Button>
                                      )}
                                    </Div>
                                    <Div>
                                      <Button
                                        variant="contained"
                                        startIcon={<OpenInNewIcon />}
                                        sx={{
                                          boxShadow: "none",
                                          height: "38px",
                                          whiteSpace: "nowrap",
                                        }}
                                        size="small"
                                        disabled={!kitchenDisplayEnabled}
                                        onClick={() =>
                                          kitchenDisplayEnabled &&
                                          showDialog({
                                            variant: "default",
                                            title: t(
                                              "pages.dsokDetails.displayNotDefinedMessage",
                                            ),
                                            onClose: () => hideDialog(),
                                          })
                                        }
                                      >
                                        {t(
                                          "pages.dsokDetails.kitchenDisplayButtonLabel",
                                        )}
                                      </Button>
                                    </Div>
                                  </Stack>
                                </>
                              )}
                            </Grid>
                            <Grid item xs={12} md={4} lg={2}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <FormControlLabel
                                  control={
                                    <Field
                                      name={`customer.locations.${location}.notificationEnabled`}
                                      component={Switch}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `customer.locations.${location}.notificationEnabled`,
                                          e.target.checked,
                                        )
                                      }
                                    />
                                  }
                                  label={
                                    <Label
                                      text={t(
                                        "pages.dsokDetails.notificationEnabled",
                                      )}
                                      helpText={t(
                                        "pages.dsokDetails.notificationEnabledHelp",
                                      )}
                                      sx={{ color: "text.primary" }}
                                      required={false}
                                    />
                                  }
                                  checked={
                                    values.customer.locations[location]
                                      .notificationEnabled
                                  }
                                />
                              </Div>
                            </Grid>
                            {values.customer.locations[location]
                              .notificationEnabled && (
                              <Grid item xs={12} md={12} lg={12}>
                                <Div sx={{ mt: 1, mb: 1 }}>
                                  <JumboTextField
                                    fullWidth
                                    name={`customer.locations.${location}.notificationText`}
                                    label={t(
                                      "pages.dsokDetails.notificationText",
                                    )}
                                  />
                                </Div>
                              </Grid>
                            )}
                            <Grid item xs={12} md={12} lg={12}>
                              <Alert variant="outlined" severity="info" sx={{ "& .MuiAlert-message": { whiteSpace: "pre-line" } }}>
                                {t(
                                  "pages.dsokDetails.infoTexts.location.textHelpText",
                                )}
                              </Alert>
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  fullWidth
                                  name={`customer.locations.${location}.headerText`}
                                  label={t("pages.dsokDetails.headerText")}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  fullWidth
                                  name={`customer.locations.${location}.infoText`}
                                  label={t("pages.dsokDetails.infoText")}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <JumboTextField
                                  fullWidth
                                  name={`customer.locations.${location}.footerText`}
                                  label={t("pages.dsokDetails.footerText")}
                                />
                              </Div>
                            </Grid>
                          </Grid>
                        </TabPanel>

                        <TabPanel
                          value="notifications"
                          sx={{ p: 0, height: "100%" }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Alert
                                severity="info"
                                variant="outlined"
                                sx={{ mt: 1, mb: 1 }}
                              >
                                <AlertTitle>
                                  {t("pages.dsokDetails.locationNotifications.title")}
                                </AlertTitle>
                                <Typography
                                  variant="body2"
                                  component="span"
                                  sx={{ display: "block", mb: 1 }}
                                >
                                  {t(
                                    "pages.dsokDetails.locationNotifications.text1",
                                  )}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  component="span"
                                  sx={{ display: "block", mb: 1 }}
                                >
                                  {t(
                                    "pages.dsokDetails.locationNotifications.text2",
                                  )}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  component="span"
                                  sx={{ display: "block", mb: 1 }}
                                >
                                  {t(
                                    "pages.dsokDetails.locationNotifications.text3",
                                  )}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  component="span"
                                  sx={{ display: "block" }}
                                >
                                  {t(
                                    "pages.dsokDetails.locationNotifications.text4",
                                  )}
                                </Typography>
                              </Alert>
                            </Grid>

                            {/* Devices notifications */}
                            <Grid item xs={12}>
                              <Typography variant="h6" sx={{ mb: 1 }}>
                                {t("pages.dsokDetails.locationNotifications.devices")}
                              </Typography>
                              <Divider sx={{ mb: 2 }} />
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t(
                                      "pages.dsokDetails.locationNotifications.email",
                                    )}
                                  </Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                      <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                                        <FormControlLabel
                                          control={
                                            <Field
                                              name={`customer.locations.${location}.notifications.devices.email.isActive`}
                                              component={Switch}
                                              onChange={(e) =>
                                                setFieldValue(
                                                  `customer.locations.${location}.notifications.devices.email.isActive`,
                                                  e.target.checked,
                                                )
                                              }
                                            />
                                          }
                                          label={t(
                                            "pages.dsokDetails.locationNotifications.isActive",
                                          )}
                                          checked={
                                            values.customer.locations?.[location]
                                              ?.notifications?.devices?.email
                                              ?.isActive || false
                                          }
                                        />
                                      </Div>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                      <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                                        <JumboTextField
                                          fullWidth
                                          name={`customer.locations.${location}.notifications.devices.email.value`}
                                          label={t(
                                            "pages.dsokDetails.locationNotifications.recipients",
                                          )}
                                          placeholder="admin@firma.sk, servis@firma.sk"
                                          disabled={
                                            !(
                                              values.customer.locations?.[location]
                                                ?.notifications?.devices?.email
                                                ?.isActive || false
                                            )
                                          }
                                        />
                                      </Div>
                                    </Grid>
                                  </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t(
                                      "pages.dsokDetails.locationNotifications.sms",
                                    )}
                                  </Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                      <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                                        <FormControlLabel
                                          control={
                                            <Field
                                              name={`customer.locations.${location}.notifications.devices.sms.isActive`}
                                              component={Switch}
                                              onChange={(e) =>
                                                setFieldValue(
                                                  `customer.locations.${location}.notifications.devices.sms.isActive`,
                                                  e.target.checked,
                                                )
                                              }
                                            />
                                          }
                                          label={t(
                                            "pages.dsokDetails.locationNotifications.isActive",
                                          )}
                                          checked={
                                            values.customer.locations?.[location]
                                              ?.notifications?.devices?.sms
                                              ?.isActive || false
                                          }
                                        />
                                      </Div>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                      <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                                        <Field
                                          name={`customer.locations.${location}.notifications.devices.sms.value`}
                                        >
                                          {({ field }) => {
                                            const raw =
                                              field.value &&
                                              typeof field.value === "string"
                                                ? field.value
                                                : "";
                                            const parts = raw
                                              .split(",")
                                              .map((p) => p.trim())
                                              .filter((p) => p.length > 0);
                                            let smsError = "";
                                            if (parts.length > 0) {
                                              const invalid = parts.find(
                                                (p) =>
                                                  !/^421\d+$/.test(p),
                                              );
                                              if (invalid) {
                                                smsError = t(
                                                  "pages.dsokDetails.locationNotifications.smsError",
                                                );
                                              }
                                            }
                                            return (
                                              <JumboTextField
                                                fullWidth
                                                {...field}
                                                error={!!smsError}
                                                helperText={smsError}
                                                label={t(
                                                  "pages.dsokDetails.locationNotifications.recipients",
                                                )}
                                                placeholder="421901234567,421902345678"
                                                disabled={
                                                  !(
                                                    values.customer.locations?.[
                                                      location
                                                    ]?.notifications?.devices
                                                      ?.sms?.isActive || false
                                                  )
                                                }
                                              />
                                            );
                                          }}
                                        </Field>
                                      </Div>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>

                          </Grid>
                        </TabPanel>
                      </Box>
                    </Stack>
                  </TabContext>
                </TabPanel>
              );
            })
          )}
        </Stack>
      </TabContext>
    </Stack>
  );

  if (hideSectionTitle) {
    return content;
  }

  return (
    <Accordion
      expanded={expanded === FIELD_NAME}
      onChange={handleChange(FIELD_NAME)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.locations")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{content}</AccordionDetails>
    </Accordion>
  );
};

export default SmartkioskLocationsField;
